import { Injectable, signal, computed, inject, effect } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import moment from 'moment';
import { BsModalService } from 'ngx-bootstrap/modal';

import { EmployeeInfoService } from '@core/services/employee-info.service';
import { PortalSettingsService } from '@core/services/portal-settings.service';
import { UtilityService } from '@core/services/utility.service';
import { ClockInOutService } from '@core/services/clock-in-out.service';
import { HomeService } from '@pages/home/services/home.service';
import { HomeApiService } from '@pages/home/services/home.api.service';

import { ClockinJobModalComponent } from '@modals/home/clockin-job-modal/clockin-job-modal.component';
import { ErrorModalComponent } from '@modals/shared/error-modal/error-modal.component';
import { ClockoutModalComponent } from '@modals/home/clockout-modal/clockout-modal.component';

import { SimpleLookupResult } from '@typeDefs/general/general';
import { ClockAction_Request } from '@typeDefs/employee/requests/employee';
import { GetClockInStatus_Result } from '@typeDefs/employee/responses/employee'; 
import { ApiResponse } from '@shared/model/api-response.model';


@Injectable({
  providedIn: 'root',
})
export class NavbarClockInService {
  // inject services
  private homeService = inject(HomeService);
  private homeApiService = inject(HomeApiService);
  private modalService = inject(BsModalService);
  private utilityService = inject(UtilityService);
  private clockinoutService = inject(ClockInOutService);
  private employeeInfo = inject(EmployeeInfoService).getEmployeeInfo();
  private portalSettings$ = inject(PortalSettingsService).portalSettings$;

  // signals
  portalSettings = toSignal(this.portalSettings$);
  private enabledLineTypesInClockIn = computed(() => this.portalSettings().EnabledLineTypesInClockIn.ConfigValue);
  canMealBreakClockIn = computed(() => {
    return this.checkCanMealBreakClockIn();
  });
 
  position = signal<GeolocationPosition | undefined>(undefined);
  requestingLocation = signal(true);
  geolocationEnabled = signal(false);
  performingClockAction = signal(false);
  showMealBreak = computed(() => this.getMealBreakSetting())
  hadMealBreakToday = signal(false);
  lastClockDisplay = signal<string>('');

  clockStatus: GetClockInStatus_Result;
  clockMoment: moment.Moment;
  mealBreakSetting: { [key: string]: string } = {};
  readonly mealBreakOptions: SimpleLookupResult[] = [
    { ComboType: null, DatabaseValue: 'N', DisplayValue: 'No' },
    { ComboType: null, DatabaseValue: 'Y', DisplayValue: 'Yes' },
  ];
  mealBreak = 'N';

  private licensedUser: { [key: string]: string } = {};

  constructor() {
    this.getClockInStatus();
  }

  getMealBreakSetting(): boolean {
    if (this.portalSettings().ClockInMealBreakMinutes.ConfigValue != null) {
      this.portalSettings().ClockInMealBreakMinutes.ConfigValue.split(';').forEach((val: string) => {
        if (val) {
          const settingParts = val.split(',');
          this.mealBreakSetting[settingParts[0] + ',' + settingParts[1]] = settingParts[2];
        }
      });
      return this.mealBreakSetting[this.employeeInfo.PRCo + ',' + this.employeeInfo.PRGroup] != null;
    }
    else
      return false; 
  }

  getClockInStatus(): void {
    const currentTime = moment();
    const currentTimeString = currentTime.format('MM/DD/YYYY h:mm:ss a');
    //reset meal break dropdown
    this.mealBreak = 'N';
    this.homeService
      .getClockInStatus(false, currentTimeString)
      .then((response: GetClockInStatus_Result) => {
        if (response != null) {
          this.performingClockAction.set(false);
          this.clockStatus = response;
          this.clockMoment = moment.utc(this.clockStatus.ClockTime);
          this.lastClockDisplay.set(this.clockMoment.format('hh:mm A'));
          this.getLocation();
          if (this.clockStatus.ClockTime != null) {
            this.clockStatus.ClockTimeString = moment(this.clockStatus.ClockTime)
              .utc()
              .format(this.utilityService.getDateFormat().toUpperCase() + ' hh:mm a');
          }
          
        }
      })
      .catch(error => {
        this.performingClockAction.set(false);
        // TODO - check if this is needed
        //this.errorMsg = error?.error?.error ?? 'An error has occured';
      });
  }

  async postClockAction($event, ignoreLocation: boolean): Promise<void> {
    $event.preventDefault();
    $event.stopPropagation();
    const requestObj: ClockAction_Request = {
      GPSLat: null,
      GPSLong: null,
      JCJMKeyID: null,
      JCJPKeyID: null,
      SMTripID: null,
      SMWorkOrderScopeID: null,
      SMPayType: null,
      EMWorkOrderKeyID: null,
      EMWorkOrderItemKeyID: null,
      EquipmentKeyID: null,
      EMCostCodeKeyID: null,
      ClockTimeString: null,
      SignatureData: null,
      FromGroupClock: false,
      KioskPass: null,
      ClockAction: null,
    };
    this.performingClockAction.set(true);
    this.licensedUser = {};
    let lineTypes = [];
    if (this.enabledLineTypesInClockIn() != null) {
      const enabledLineTypesInClockInList = this.enabledLineTypesInClockIn().split(',');
      if (enabledLineTypesInClockInList.includes('S')) {
        this.licensedUser = await this.homeService.getLicenseCounts(this.employeeInfo.PRCo, this.employeeInfo.Employee);
      }
      lineTypes = this.clockinoutService.getEnabledLineTypes(this.licensedUser, this.employeeInfo.IsActiveTechnician);
    }

    if (!this.clockStatus.ClockAction && lineTypes.length > 0) {
      const initialState = {
        portalSettings: this.portalSettings(),
        employeeInfo: this.employeeInfo,
        lastClockinType: this.clockStatus.LastUsedClockinType ?? 'J',
        lineTypes: lineTypes,
        backdrop: 'static',
      };

      const modalRef = this.modalService.show(ClockinJobModalComponent, { initialState, ignoreBackdropClick: true, keyboard: true });

      modalRef.onHide.subscribe(() => {
        const result = modalRef.content;
        if (result.userAction == 'cancel') {
          this.performingClockAction.set(false);
        } else {
          if (result != null && result.userAction == 'Clock In') {
            if (!ignoreLocation && this.position()?.coords) {
              requestObj.GPSLat = this.position().coords.latitude;
              requestObj.GPSLong = this.position().coords.longitude;
            }
            if (result.formData.lineType == 'J') {
              if (result.formData.selectedJob != null) {
                requestObj.JCJMKeyID = result.formData.selectedJob.JCJMKeyID;
              }
              if (result.formData.selectedPhase != null) {
                requestObj.JCJPKeyID = result.formData.selectedPhase.JCJPKeyID;
              }
              if (result.formData.selectedEquipment != null) {
                requestObj.EquipmentKeyID = result.formData.selectedEquipment?.KeyID;
              }
            } else if (result.formData.lineType == 'S') {
              requestObj.SMTripID = result.formData.workOrder?.SMTripID;
              requestObj.SMWorkOrderScopeID = result.formData.scope?.SMWorkOrderScopeID;
              requestObj.SMPayType = result.formData.payType?.DatabaseValue;
            } else if (result.formData.lineType == 'M') {
              requestObj.EMWorkOrderKeyID = result.formData.selectedEMWorkOrder?.KeyID;
              requestObj.EMWorkOrderItemKeyID = result.formData.selectedEMWorkOrderItem?.KeyID;
              requestObj.EquipmentKeyID = result.formData.selectedEquipment?.KeyID;
              requestObj.EMCostCodeKeyID = result.formData.selectedEMCostCode?.KeyID;
            }
            const currentTime = moment();
            requestObj.ClockTimeString = currentTime.format('MM/DD/YYYY h:mm:ss a');
            requestObj.MealBreak = this.mealBreak == 'Y';
            this.homeService.postClockAction(requestObj).then((response: ApiResponse) => {
              if (response.success) {
                this.getClockInStatus();
              } else {
                this.performingClockAction.set(false);
                const initialState = {
                  modalData: { modalTitle: 'An Error Occurred', modalMessage: response.error },
                  backdrop: 'static',
                };
                this.modalService.show(ErrorModalComponent, { initialState, ignoreBackdropClick: true, keyboard: true });
              }
            });
          } else {
            this.performingClockAction.set(false);
          }
        }
      });
    } else {
      if (this.portalSettings().RequireSignClockOut.ConfigValue == '1' && this.clockStatus.ClockAction) {
        const initialState = {
          portalSettings: this.portalSettings(),
          employeeInfo: this.employeeInfo,
          backdrop: 'static',
        };

        const modalRef = this.modalService.show(ClockoutModalComponent, { initialState, ignoreBackdropClick: true, keyboard: true });

        modalRef.onHide.subscribe(() => {
          const result = modalRef.content.userAction;
          if (result == 'close') {
            this.performingClockAction.set(false);
          } else {
            if (!ignoreLocation && this.position()?.coords) {
              requestObj.GPSLat = this.position().coords.latitude;
              requestObj.GPSLong = this.position().coords.longitude;
            }
            requestObj.SignatureData = result;
            const currentTime = moment();
            requestObj.MealBreak = this.mealBreak == 'Y';
            requestObj.ClockTimeString = currentTime.format('MM/DD/YYYY h:mm:ss a');
            this.homeService.postClockAction(requestObj).then((response: ApiResponse) => {
              if (response.success) {
                this.getClockInStatus();
              } else {
                this.performingClockAction.set(false);
                const initialState = {
                  modalData: { modalTitle: 'An Error Occurred', modalMessage: response.error },
                  backdrop: 'static',
                };
                this.modalService.show(ErrorModalComponent, { initialState, ignoreBackdropClick: true, keyboard: true });
              }
            });
          }
        });
      } else {
        if (!ignoreLocation && this.position()?.coords) {
          requestObj.GPSLat = this.position().coords.latitude;
          requestObj.GPSLong = this.position().coords.longitude;
        }
        const currentTime = moment();
        requestObj.MealBreak = this.mealBreak == 'Y';
        requestObj.ClockTimeString = currentTime.format('MM/DD/YYYY h:mm:ss a');
        this.homeService.postClockAction(requestObj).then((response: ApiResponse) => {
          if (response.success) {
            this.getClockInStatus();
          } else {
            this.performingClockAction.set(false);
            const initialState = {
              modalData: { modalTitle: 'An Error Occurred', modalMessage: response.error },
              backdrop: 'static',
            };
            this.modalService.show(ErrorModalComponent, { initialState, ignoreBackdropClick: true, keyboard: true });
          }
        });
      }
    }
  }

  private checkCanMealBreakClockIn() {
    const mealBreakSetting = this.mealBreakSetting;
    if (mealBreakSetting[this.employeeInfo.PRCo + ',' + this.employeeInfo.PRGroup] != null) {
      const mealBreakThreshold = parseInt(mealBreakSetting[this.employeeInfo.PRCo + ',' + this.employeeInfo.PRGroup]);
      //convert utc clock in time to local time
      const clockMoment = this.clockMoment;
      if (!clockMoment) return true;
      const lastClockOut = moment(clockMoment.format('YYYY-MM-DD HH:mm:ss'));
      const minutesElapsed = moment().diff(lastClockOut, 'minutes');
      if (minutesElapsed < mealBreakThreshold) return false;
    }
    return true;
  }

  getLocation(): void {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(position => {
        this.requestingLocation.set(false);
        this.geolocationEnabled.set(true);
        this.position.set(position);
      });
    } else {
      this.requestingLocation.set(false);
    }
  }
}